import React, {createRef, memo, useContext, useEffect, useState} from 'react';
import { useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import { Stack } from '@mui/material';
import { Icon, IconButton, Switch, Typography, useTheme } from "@material-ui/core";
import {
  Email,
  Flag,
  FlightTakeoff,
  Highlight, LocalGasStation,
  SaveAlt, SettingsEthernet,
  WarningTwoTone,
} from "@material-ui/icons";
import {MoreTime, SendTimeExtension} from "@mui/icons-material";
import {ChecklistIcon, FfmIcon, FlightScheduleIcon} from "../../../icons";
import {
  defaultComponentsScroll,
  FullWidthFilter,
  MaterialTableBody
} from '../../../controls/Material-Table-Components';
import SystemContext from "../../../SystemContext";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import { LightTooltip } from "../../../controls";
import RefreshCheck from '../../../controls/RouteView2/RefreshCheck';
import IconButtonMenu from "../../../controls/IconButtonMenu";
import {ColumnWidth, dowNumberSearch} from "../../../blocks/ColumnHelper";
import useDimensions from "../../../blocks/useDimensions";
import { useFlights } from "../../../services/flights";
import { useUpdateFlight } from "../../../services/flights";
import { useUpdateTrip } from "../../../services/trips";
import { useSlotInfo } from "../../../services/slots";
import useSaveTripLegServices from '../../../services/trips/useSaveTripLegServices';
import {fetch as fetchDefaultServiceTypes} from "../../../services/settings/useAirportServiceTypes";
import {fetch as fetchTripLegServices} from "../../../services/trips/useTripLegServices";
import AirportSelectionDialog from "../../Trips/components/AirportSelectionDialog";
import CustomerSelectionDialog from "../../Trips/components/CustomerSelectionDialog";
import dialogs,
{ EditCrewDialog,
  EditPaxDialog,
  EditServicesDialog,
  EditCateringDialog,
  EditTripDialog,
} from "../../Trips/components/TripListDialogs";
import AircraftFuelLogDialog from "../../Aircraft/components/AircraftFuelLogDialog";
import FlightTooltip from "./FlightTooltip";
import EditNotesDialog from "./EditNotesDialog";
import NotesViewer from "../../../controls/RouteView2/TripPopupScreens/NotesScreen";
import NotesCount from "./NotesCount";
import CargoGrossWeight from "./CargoGrossWeight";
import EditDeadloadDialog from "./EditDeadLoadDialog";
import EditChecklistDialog from "./EditChecklistDialog";
import EditFlightCargoDialog from "./EditFlightCargoDialog";
import EditFlightDocumentsDialog from "./EditFlightDocumentsDialog";
import PermitsScreen from "../../../controls/RouteView2/TripPopupScreens/PermitsScreen";
import EditPermitsDialog from "./EditPermitsDialog";
import EditMapDialog from "./EditMapDialog";
import EditFlightSlotsDialog from "./EditFlightSlotsDialog";
import FlightManifestsDialog from "./FlightManifestsDialog";
import FuelScreen from "../../../controls/RouteView2/TripPopupScreens/FuelScreen";
import FuelPolicyDialog from '../../../modules/Fuel/components/FuelOrderDialogs/FuelPolicyDialog';
import EditFlightMessagesDialog from "./EditFlightMessagesDialog";
import SpecialLoads from "./SpecialLoads";
import ViewBooking from "../../Booking/components/ViewBooking";
import BuildTruckRouteIconFromLegs from "../../Trips/utils/BuildTruckRouteIconFromLegs";
import AirportIcao from '../../Airports/components/AirportIcao';

const AccessControl = require("../../../AccessControl");
const apiSitePrefix = getApiSitePrefix();

const formatChars={
  "U": "[A-Za-z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "n": "-",
  "x": "."
}

const flightNumberSearch = (fieldName) => {
  return  (term, rowData) => {
    if (!__.has(rowData, fieldName)) return __.isEmpty(term);
    const airlineCode = rowData.airlineCode;
    const flightNo = rowData.flightNo;
    const flightNumber = airlineCode + flightNo;
    if (airlineCode && airlineCode.startsWith(term.toUpperCase()))
      return true;
    if (flightNo && flightNo.startsWith(term.toUpperCase()))
      return true;
    if (flightNumber && flightNumber.startsWith(term.toUpperCase()))
      return true;
    return flightNumber === term.toUpperCase();
  }
}

const FlightList = (_props) => {
  const {isAuthenticated, user} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const props = {
    ..._props
  }
  const classes = props.classes;
  const colours = SystemData.colours;

  const operatorName = __.startCase(__.toLower(process.env.REACT_APP_COMPANY)) || "Magma";

  const tableRef = createRef({state: {lastEditingRow: false}});
  const month = props.month;
  const year = props.year;

  const queryClient = useQueryClient();
  const extraKeys = [year, month];

  const handleUpdateFlight = useUpdateFlight();
  const handleSaveServices = useSaveTripLegServices();
  const handleUpdateTrip = useUpdateTrip();

  const flights = useFlights({tableRef, queryClient, extraKeys});
  const [state] = useState({
    canEdit: props.canEdit,
    canEditFuelLogs: props.canEditFuelLogs,
    year: props.year,
    month: props.month
  });

  const [selectAirport, setSelectAirport] = useState({
    open: false,
    airport: null,
    mode: "download"
  });

  const [selectCustomer, setSelectCustomer] = useState({
    open: false,
    airline: null,
    mode: "download"
  });

  const [defaultValues, setDefaultValues] = useState(null);
  const [scheduleDefaultValues, setScheduleDefaultValues] = useState(null);

  const handleTripActionEditFuelPolicy = (params) => {
    eventBus.trigger("FuelPolicyDialog.open", params);
  }

  useEffect(()=> {
    if (eventBus) {
      eventBus.bind('FlightList.TripAction.RefreshList', refreshList);
      eventBus.bind('RouteView.TimeWindow.TripAction.editFuelPolicy', handleTripActionEditFuelPolicy);
      return ()=>{
        eventBus.unbind('FlightList.TripAction.RefreshList', refreshList);
        eventBus.unbind('RouteView.TimeWindow.TripAction.editFuelPolicy', handleTripActionEditFuelPolicy);
      }
    }
  }, [eventBus]);

  let selectedRow = -1;

  const listActionOptions = [
    {action: "Download ACMI Operator Flight Schedule", itemLabel: "Download ACMI Operator Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Downloaded"},
    // {action: "Download Air Atlanta Flight Schedule", itemLabel: "Download Air Atlanta Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Downloaded"},
    // {action: "Download Atlas Air Flight Schedule", itemLabel: "Download Atlas Air Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Downloaded"},
    {action: "Download Airport Flight Schedule", itemLabel: "Download Airport Flight Schedule", itemIcon: <Highlight/>, historySuffix: " - Downloaded"},
    {action: "Download Customer Flight Schedule", itemLabel: "Download Customer Flight Schedule", itemIcon: <Highlight/>, historySuffix: " - Downloaded"},
    {action: "Download Fuel Hours Report", itemLabel: "Download Fuel Hours Report", itemIcon: <LocalGasStation/>, historySuffix: " - Downloaded"},
    {action: "Download Flight Delay Report", itemLabel: "Download Flight Delay Report", itemIcon: <MoreTime/>, historySuffix: " - Downloaded"},
    {action: "Download Block Hours Report", itemLabel: "Download Block Hours Report", itemIcon: <SettingsEthernet/>, historySuffix: " - Downloaded"},
    {action: "Download Dispatch Checklist Report", itemLabel: "Download Dispatch Checklist Report", itemIcon: <SendTimeExtension/>, historySuffix: " - Downloaded"},
  ];

  const sendActionOptions = [
    {action: "Email ACMI Operator Flight Schedule", itemLabel: "Email ACMI Operator Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Emailed"},
    {action: "Email Air Atlanta Flight Schedule", itemLabel: "Email Air Atlanta Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Emailed"},
    {action: "Email Atlas Air Flight Schedule", itemLabel: "Email Atlas Air Flight Schedule", itemIcon: <FlightTakeoff/>, historySuffix: " - Emailed"},
    {action: "Email Airport Flight Schedule", itemLabel: "Email Airport Flight Schedule", itemIcon: <Highlight/>, historySuffix: " - Emailed"},
    {action: "Email Customer Flight Schedule", itemLabel: "Email Customer Flight Schedule", itemIcon: <Highlight/>, historySuffix: " - Emailed"},
    {action: "Send Daily Flight Schedule to Subscribers", itemLabel: "Send Daily Flight Schedule to Subscribers", itemIcon: <FlightScheduleIcon style={{width: 20, height: 20, color: "orange" }}/>, historySuffix: " - Emailed"},
  ];

  const getDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/tripsheet/defaults`);
      if (response.data && response.data.success) {
        setDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Trip sheet Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Trips sheet Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Trips' Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Trips sheet Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Trips sheet Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Trips sheet Defaults. Server is not responding`);
    }
  }

  const getScheduleDefaultValues = async (scheduleType) => {
    try {
      const response = await axios.get(`${apiSitePrefix}/flightSchedule/defaults`);
      if (response.data && response.data.success) {
        setScheduleDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Flight Schedule Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Flight Schedule Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Flight Schedule Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Flight Schedule Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Flight Schedule Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Flight Schedule Defaults. Server is not responding`);
    }
  }

  const refreshList = async () => {
    queryClient.invalidateQueries(["Flights"]);
  }

  async function executeListAction(params) {
    if (params) {
      switch (params.selectedOption) {
        case "Download ACMI Operator Flight Schedule":
          setSelectCustomer({...selectCustomer, open: true, airline: true, mode: "download"});
          break;
        case "Download Air Atlanta Flight Schedule":
          downloadFlightSchedule("airline", "CC", true);
          break;
        case "Download Atlas Air Flight Schedule":
          downloadFlightSchedule("airline", "5Y", true);
          break;
        case "Download Airport Flight Schedule":
          setSelectAirport({...selectAirport, open: true, mode: "download"});
          break;
        case "Download Customer Flight Schedule":
          setSelectCustomer({...selectCustomer, open: true, airline: false, mode: "download"});
          break;
        case "Download Fuel Hours Report":
          downloadFlightSchedule("hoursFuelReport", "", true);
          break;
        case "Download Block Hours Report":
          downloadFlightSchedule("hoursBlockReport", "", true);
          break;
        case "Download Flight Delay Report":
          downloadFlightSchedule("delayReport", "", true);
          break;
        case "Download Dispatch Checklist Report":
          downloadFlightSchedule("dispatchChecklist", params.tripLegUuid, true);
          break;
        default:
          break;
      }
    }
  }

  async function executeEmailAction(params) {
    if (params) {
      switch (params.selectedOption) {
        case "Email ACMI Operator Flight Schedule":
          setSelectCustomer({...selectCustomer, open: true, airline: true, mode: "email"});
          break;
        case "Email Air Atlanta Flight Schedule":
          downloadFlightSchedule("airline", "CC", false);
          break;
        case "Email Atlas Air Flight Schedule":
          downloadFlightSchedule("airline", "5Y", false);
          break;
        case "Email Airport Flight Schedule":
          setSelectAirport({...selectAirport, open: true, mode: "email"});
          break;
        case "Email Customer Flight Schedule":
          setSelectCustomer({...selectCustomer, open: true, mode: "email"});
          break;
        case "Send Daily Flight Schedule to Subscribers":
          sendDailyFlightSchedule();
          break;
        default:
          break;
      }
    }
  }

  const executeRowAction = async (params) => {
    if (params) {
      const rowData = params.rowData;
      const mode = params.mode;
      const message = params.message;
      const flightSheet = !params.trip;
      let url = null;

      switch (params.selectedOption) {
        case "Generate Trip Sheet":
          url = `${apiSitePrefix}/trip/tripsheet/${rowData.tripUuid}/${flightSheet ? rowData.tripLegUuid : "null"}`;
          break;
        default:
          break;
      }

      if (url) {
        try {
          SystemData.toggleLoadingProgress(true, message ? message : "");
          const response = await axios.get(url, {
            headers: {
              //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
              'Access-Control-Allow-Origin': '*',
            }
          });
          SystemData.toggleLoadingProgress(false);

          if (response && response.data) {
            if (response.data.status) {
              if (mode && mode === "email") {
                const fileName = response.data.downloadFileName;
                const flightDate = moment(rowData.flightDate, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY");
                const subjectPrefix = `${rowData.tripPrefix}${rowData.tripNo} // ${flightSheet ? `${operatorName} Flight Sheet` :  `${operatorName} Aviation Trip Sheet`}`;
                const subject = `${subjectPrefix} // ${rowData.airlineCode}${rowData.flightNo} // ${rowData.originAirportCode} - ${rowData.destinationAirportCode} // ${flightDate} // ${rowData.aircraftReg}`;
                const attachmentDescription = `${flightSheet ? "Flight" : "Trip"} Sheet for ${rowData.airlineCode}${rowData.flightNo} / ${rowData.originAirportCode} - ${rowData.destinationAirportCode} / ${flightDate} / ${rowData.aircraftReg}`;
                const emailRecord = {
                  recipients: "",
                  carbonCopy: defaultValues && __.has(defaultValues, "ccList") ? defaultValues.ccList : "",
                  subject: subject,
                  templateType: "TRIP_SHEET",
                  templateUuid: "dummyTemplateUuid",
                  attachmentDescription: attachmentDescription,
                  attachmentUrl: response.data.excelSignedUrl,
                  fileName: fileName.replaceAll("/",""),
                  attachments: "Trip Sheet, DLS, NOTOC",
                  repeatData: [],
                  contactRole: "TRIP SHEET",
                  tripLegUuid: rowData.tripLegUuid,
                  additionalAttachments: response.data.additionalAttachments,
                  from: defaultValues && __.has(defaultValues, "from") ? defaultValues.from : "",
                }
                SystemData.sendEmail(emailRecord, `Send ${flightSheet ? `Flight Sheet for ${rowData.airlineCode}${rowData.flightNo}`: `Trip Sheet for ${rowData.tripPrefix}${rowData.tripNo}`}`, true);
              } else {
                const fileName = response.data.downloadFileName;
                let a = document.createElement('a');
                a.href = response.data.excelSignedUrl;
                a.download = fileName;
                a.style.display = "none";
                document.body.appendChild(a);
                a.click();
                setTimeout(() => { document.body.removeChild(a); }, 0);
              }
            } else {
              if (response.data.error) {
                switch (params.selectedOption) {
                  case "Create Trip Folders":
                  case "Confirm EuroCargo Draft Reconciliation":
                    SystemData.openAlert(
                      "Error",
                      `${params.selectedOption} failed to action due to the following error: ${response.data.error}`
                    );
                    break;
                  default:
                    SystemData.openAlert(
                      "Download Error",
                      `The ${params.selectedOption} report could not be downloaded due to the following error: ${response.data.error}`
                    );
                }
              } else {
                SystemData.openAlert(
                  "Info",
                  `${params.selectedOption} Completed`
                )
              }
            }
          }

        } catch (err) {
          SystemData.toggleLoadingProgress(false);
          let errorText = "due to an unknown error";
          if (err.response && err.response.data && err.response.data.error) {
            errorText = `, ${err.response.data.errorMessage}`;
          }
          switch (params.selectedOption) {
            default:
              SystemData.openAlert(
                "Download Error",
                `The ${params.selectedOption} report could not be downloaded ${errorText}`
              );
          }
        }
      }
      // queryClient.invalidateQueries(['Trips', ...extraKeys]);
    }
  }

  async function downloadFlightSchedule(target, filter, download = true, companyUuid = null, companyName = "", excludeAcmi = false) {

    if (!target)
      return;

    const startDateMoment = moment(`${props.year}-${props.month}-01`);
    const startDate = startDateMoment.isValid() ? startDateMoment.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
    const endDate = moment(startDate).endOf("month").format("YYYY-MM-DD");
    const route = target === "delayReport" ? "delayReport" : target === "hoursFuelReport" ? "hoursFuelReport" : target === "hoursBlockReport" ? "hoursBlockReport" : target === "airline" ? "lessorschedule" : target==="customer"?"customerschedule":"airportschedule";

    let url = `${apiSitePrefix}/trip/${route}/${startDate}/${endDate}/${filter}${excludeAcmi ? "/excludeAcmi" : ""}`;

    if (target === "dispatchChecklist")
      url = `${apiSitePrefix}/flight/cleanFlightData/${filter ?? `${startDate}/${endDate}`}`;

    if (url) {
      try {
        SystemData.toggleLoadingProgress(true, `Preparing Flights Report ...`);
        const response = await axios.get(url, {
          headers: {
            //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
            'Access-Control-Allow-Origin': '*',
          }
        });

        if (response && response.data) {
          SystemData.toggleLoadingProgress(false);
          if (response.data.status) {
            if (!download) {
              let templateType;
              let subject;
              let attachmentDescription;
              const displayDateFormat = "DD MMM YYYY";
              const scheduleFrom = startDateMoment.isValid() ? startDateMoment.format(displayDateFormat) : moment().format(displayDateFormat);
              const scheduleTo = moment(scheduleFrom, displayDateFormat).endOf("month").format(displayDateFormat);
              const fileName = response.data.downloadFileName;
              switch (target) {
                case "airline":
                  subject = `${operatorName} Flight Schedule // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "OPERATOR_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                case "customer":
                  subject = `${operatorName} Flight Schedule // ${companyName} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "CUSTOMER_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule for ${companyName} effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                default:
                  subject = `${operatorName} Flight Schedule // ${filter} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "AIRPORT_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule via ${filter} effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
              }

              const emailRecord = {
                target: target,
                targetFilter: target === "airport" && companyUuid ? companyUuid : filter,
                recipients: "",
                carbonCopy: scheduleDefaultValues && __.has(scheduleDefaultValues, "ccList") ? scheduleDefaultValues.ccList : "",
                subject: subject,
                templateType: templateType,
                templateUuid: "dummyTemplateUuid",
                attachmentDescription: attachmentDescription,
                attachmentUrl: response.data.excelSignedUrl,
                fileName: fileName.replaceAll("/",""),
                repeatData: [],
                contactRole: "FLIGHTS",
                airportCode: target === "airport"?filter:undefined,
                from: scheduleDefaultValues && __.has(scheduleDefaultValues, "from") ? scheduleDefaultValues.from : "",
              }
              SystemData.sendEmail(emailRecord, `Send Flight Schedule`, true);

              return;
            }

            const fileName = response.data.downloadFileName;
            let a = document.createElement('a');
            a.href = response.data.excelSignedUrl;
            a.download = fileName;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { document.body.removeChild(a); }, 0);
          } else {
            SystemData.openAlert(
              "Download Error",
              `The report could not be downloaded due to the following error: ${response.data.error}`
            );
          }
        }
      } catch (err) {
        SystemData.toggleLoadingProgress(false);
        SystemData.openAlert(
          "Download Error",
          `The Schedule report could not be downloaded due to the following unexpected error: ${err}`
        );
      }
    }
  }

  const sendDailyFlightSchedule = async () => {

    SystemData.confirm(
      "Send Daily Flight Schedule",
      "This function sends the daily flight schedule to all customers and service providers subscribed to the FLIGHTS message.",
      [
        {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      async (response) => {
        if (response) {
          const url = `${apiSitePrefix}/flights/senddialyflights`;

          if (url) {
            try {
              SystemData.toggleLoadingProgress(true,"Sending Daily Flight Schedule");
              const response = await axios.get(url, {
                headers: {
                  //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
                  'Access-Control-Allow-Origin': '*',
                }
              });
              SystemData.toggleLoadingProgress(false,"");
              if (response && response.data) {
                if (response.data.success) {
                  let message = `Daily flight schedule was sent to all FLIGHTS subscribers`;
                  if (response.data.error && response.data.error !== "")
                    message += `(${response.data.error})`;
                  SystemData.openAlert(
                    "Send Confirmation",
                    message
                  );
                } else {
                  SystemData.openAlert(
                    "Daily Flight Schedule Error",
                    `The Daily Flight Schedule could not be sent due to the following error: ${response.data.error}`
                  );
                }
              }
            } catch (err) {
              SystemData.toggleLoadingProgress(false,"");
              SystemData.openAlert(
                "Daily Flight Schedule Error",
                `The Daily Flight Schedule  could not be sent due to the following unexpected error: ${err}`
              );
            }
          }
        }
      },
      {secondMessage: "Are you sure you wish to re-send the Daily Flight Schedule to all FLIGHTS subscribers (CMS automatically sends the schedule every day at 17:00)?"}
    );
  }

  const handleEditTripAction = async (tripUuid, tripNumber) => {
    const url = `${apiSitePrefix}/trip/get/${tripUuid}`;

    if (url) {
      try {
        const response = await axios.get(url, {
          headers: {
            //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
            'Access-Control-Allow-Origin': '*',
          }
        });

        if (response && response.data) {
          eventBus.trigger("EditTripDialog.open", response.data);
        } else {
          SystemData.openAlert(
            "Get Trip Error",
            `Could not get data for Trip ${tripNumber}`
          );
        }
      } catch (err) {
        let errorText = "due to the following due to an unknown error";
        if (err.response && err.response.data && err.response.data.error) {
          errorText = `, ${err.response.data.errorMessage}`;
        }
        SystemData.openAlert(
          "Edit Trip Error",
          `Could not edit trip ${tripNumber} ${errorText}`
        );
      }
    }
  }

  const handleFuelLogDialogChange = (event) => {
    if (event.state === "cancel") {
      setAircraftFuelLogDialogProps ({
        ...aircraftFuelLogDialogProps,
        open: false,
      });
    }
    if (event.state === "save") {
      setAircraftFuelLogDialogProps ({
        ...aircraftFuelLogDialogProps,
        open: false,
      });
    }
  }

  const [aircraftFuelLogDialogProps, setAircraftFuelLogDialogProps] = useState({
    open: false,
    onChange: handleFuelLogDialogChange
  });

  const tooltipAction = async (selectedTool, record, additionalActionData) => {

    if (record && record.transportType !== "PRIMARY" && ["fuel", "catering", "partners", "crew", "pax", "checklist", "permits", "slots", "dispatchchecklist"].includes(selectedTool))
      return;

    switch (selectedTool) {
      case "notes":
        eventBus.trigger("EditNotesDialog.open", record);
        break;
      case "fuel":
        setAircraftFuelLogDialogProps({
          ...aircraftFuelLogDialogProps,
          open: true,
          legRecord: record,
          onChange: handleFuelLogDialogChange,
          canEdit: state.canEdit || state.canEditFuelLogs
        })
        break;
      case "catering":
        if (!record.crew || (Array.isArray(record.crew) && record.crew.length < 1)){
          SystemData.openAlert("Catering Prerequisites", "Crew numbers must be captured in order to generate catering orders");
        } else {
          let supplierUuid = null;
          let orderTemplateUuid = null;
          const defaultServiceTypes = await fetchDefaultServiceTypes({queryClient, extraKeys: [record.originAirportCode], useCache: false});
          if (defaultServiceTypes && Array.isArray(defaultServiceTypes)) {
            const cateringService = __.find(defaultServiceTypes, {serviceCode: "CATERING"});
            if (cateringService && __.has(cateringService, "companyUuid"))
              supplierUuid = cateringService.companyUuid;
            if (cateringService && __.has(cateringService, "orderEmailTemplate"))
              orderTemplateUuid = cateringService.orderEmailTemplate;
          }

          const tripLegServices = await fetchTripLegServices({queryClient, extraKeys: [record.tripLegUuid], useCache: false});
          const cateringService = __.find(tripLegServices, {serviceCode: "CATERING"});
          if (cateringService && cateringService.serviceProviderUuid) {
            supplierUuid = cateringService.serviceProviderUuid;
          }
          if (supplierUuid)
            record["supplierUuid"] = supplierUuid;
          if (orderTemplateUuid)
            record["orderTemplateUuid"] = orderTemplateUuid;

          eventBus.trigger("EditCateringDialog.open", record);
        }
        break;
      case "partners":
        eventBus.trigger("EditServicesDialog.open", record);
        break;
      case "crew":
        eventBus.trigger("EditCrewDialog.open", record);
        break;
      case "pax":
        eventBus.trigger("EditPassengerDialog.open", record);
        break;
      case "checklist":
        eventBus.trigger("EditChecklistDialog.open", record);
        break;
      case "dispatchchecklist":
        executeListAction({selectedOption: "Download Dispatch Checklist Report", tripLegUuid: record.tripLegUuid})
        break;
      case "cargo":
        if (additionalActionData)
          record["specialLoads"] = additionalActionData;
        eventBus.trigger("EditCargoDialog.open", record)
        break;
      case "slots":
        eventBus.trigger("EditSlotsDialog.open", record)
        break;
      case "permits":
        eventBus.trigger("EditPermitsDialog.open", record);
        break;
      case "map":
        handleEditTripAction(record.tripUuid, `${record.tripPrefix}-${record.tripNo}`);
        break;
      case "reports":
        if (additionalActionData) {
          const trip = !(additionalActionData === "Download Flight Sheet Report" || additionalActionData === "Email Flight Sheet Report");
          const message = `Preparing ${!trip ? "Flight" : "Trip"} Sheet report for ${!trip ? record.airlineCode + record.flightNo : record.tripPrefix+record.tripNo} ...`;
          if (additionalActionData === "Download Trip Sheet Report" || additionalActionData === "Download Flight Sheet Report") {
            executeRowAction({rowData: record, selectedOption: "Generate Trip Sheet", mode: "download", message: message, trip: trip});
          } else if (additionalActionData === "Email Trip Sheet Report" || additionalActionData === "Email Flight Sheet Report") {
            executeRowAction({rowData: record, selectedOption: "Generate Trip Sheet", mode: "email", message: message, trip: trip});
          }
        }
        break;
      case "documents":
        eventBus.trigger("EditFlightDocuments.open", record);
        break;
      case "timing":
        eventBus.trigger("EditMapDialog.open", record);
        break;
      case "messages":
        eventBus.trigger("EditFlightMessages.open", record);
        break;
      default:
    }
  }

  const handleRowClick = (event, rowData) => {
    if (rowData.tableData.id >= 0) {
      selectedRow = rowData.tableData.id;
    }
  }

  const renderSlotInfo = (inboundSlot, outboundSlot, origin, destination) => {
    if (!inboundSlot && !outboundSlot)
      return <span>No slot information is available</span>

    const dateTimeFormat = "YYYY-MM-DD HH:mm:ss";
    const displayDateFormat = "DD/MM/YYYY";

    return (
      <div>
        <div>
          <div>
            {outboundSlot
              ?
              <table style={{borderCollapse: "collapse", width: "100.0%"}} border={"1"}>
                <tbody>
                  <tr>
                    <td colSpan={"7"}><p><strong>{`Outbound Slot at ${origin}`}</strong></p></td>
                  </tr>
                  <tr>
                    <td style={{textAlign: "center", width: "12%"}}>
                      <p><strong>ReferenceF</strong></p>
                    </td>
                    <td style={{textAlign: "center", width: "12%"}}>
                      <p><strong>Status</strong></p>
                    </td>
                    <td style={{textAlign: "center", width: "12%"}}>
                      <p><strong>Period From</strong></p>
                    </td>
                    <td style={{textAlign: "center", width: "12%"}}>
                      <p><strong>Period To</strong></p>
                    </td>
                    <td style={{textAlign: "center", width: "12%"}}>
                      <p><strong>Time</strong></p>
                    </td>
                    <td style={{textAlign: "center", width: "12%"}}>
                      <p><strong>Service</strong></p>
                    </td>
                    <td style={{textAlign: "center", width: "12%"}}>
                      <p><strong>Aircraft</strong></p>
                    </td>
                  </tr>
                  <tr>
                    <td style={{textAlign: "center", width: "12%"}}>{outboundSlot.slotReference}</td>
                    <td style={{textAlign: "center", width: "12%"}}>{outboundSlot.status}</td>
                    <td style={{textAlign: "center", width: "12%"}}>{moment(outboundSlot.slotPeriodFrom, dateTimeFormat).format(displayDateFormat)}</td>
                    <td style={{textAlign: "center", width: "12%"}}>{moment(outboundSlot.slotPeriodTo, dateTimeFormat).format(displayDateFormat)}</td>
                    <td style={{textAlign: "center", width: "12%"}}>{outboundSlot?.time?.length > 5 ? outboundSlot?.time.substr(0,5) : inboundSlot?.time}</td>
                    <td style={{textAlign: "center", width: "12%"}}>{outboundSlot.service}</td>
                    <td style={{textAlign: "center", width: "12%"}}>{outboundSlot.aircraftTailNumber}</td>
                  </tr>
                </tbody>
              </table>
              : <></>}
          </div>
        </div>
        <p></p>
        <div>
          {inboundSlot
            ?
            <table style={{borderCollapse: "collapse", width: "100.0%"}} border={"1"}>
              <tbody>
                <tr>
                  <td colSpan={"7"}><p><strong>{`Inbound Slot at ${destination}`}</strong></p></td>
                </tr>
                <tr>
                  <td style={{textAlign: "center", width: "12%"}}>
                    <p><strong>Reference</strong></p>
                  </td>
                  <td style={{textAlign: "center", width: "12%"}}>
                    <p><strong>Status</strong></p>
                  </td>
                  <td style={{textAlign: "center", width: "12%"}}>
                    <p><strong>Period From</strong></p>
                  </td>
                  <td style={{textAlign: "center", width: "12%"}}>
                    <p><strong>Period To</strong></p>
                  </td>
                  <td style={{textAlign: "center", width: "12%"}}>
                    <p><strong>Time</strong></p>
                  </td>
                  <td style={{textAlign: "center", width: "12%"}}>
                    <p><strong>Service</strong></p>
                  </td>
                  <td style={{textAlign: "center", width: "12%"}}>
                    <p><strong>Aircraft</strong></p>
                  </td>
                </tr>
                <tr>
                  <td style={{textAlign: "center", width: "12%"}}>{inboundSlot.slotReference}</td>
                  <td style={{textAlign: "center", width: "12%"}}>{inboundSlot.status}</td>
                  <td style={{textAlign: "center", width: "12%"}}>{moment(inboundSlot.slotPeriodFrom, dateTimeFormat).format(displayDateFormat)}</td>
                  <td style={{textAlign: "center", width: "12%"}}>{moment(inboundSlot.slotPeriodTo, dateTimeFormat).format(displayDateFormat)}</td>
                  <td style={{textAlign: "center", width: "12%"}}>{inboundSlot?.time?.length > 5 ? inboundSlot?.time.substr(0,5) : inboundSlot?.time}</td>
                  <td style={{textAlign: "center", width: "12%"}}>{inboundSlot.service}</td>
                  <td style={{textAlign: "center", width: "12%"}}>{inboundSlot.aircraftTailNumber}</td>
                </tr>
              </tbody>
            </table>
            : <>/</>}
        </div>
        <p></p>
      </div>
    );
  }

  const renderIndicator = (indicators, tool, rowData) => {

    if (rowData && rowData.transportType !== "PRIMARY")
      return (<></>)

    const indicator = __.has(indicators, tool) ? indicators[tool] : {color: "none", status: "NOT REQUIRED"};
    let color = indicator.color;

    if (indicator.status === "NOT REQUIRED")
      return <></>;

    if (indicator.status === "REQUIRED")
      return ( <div style={{textAlign: "center"}}><WarningTwoTone style={{width: 24, height: 24, color: color }} onClick={() => {tooltipAction(tool, rowData) }}/></div>);

    if (indicator.status === "INCOMPLETE")
      return ( <div style={{textAlign: "center"}}><WarningTwoTone style={{width: 24, height: 24, color: color}} onClick={() => {tooltipAction(tool, rowData) }}/></div>);

    if (indicator.status === "COMPLETED")
      return (<></>)
      // return ( <div style={{textAlign: "center"}}><Flag style={{width: 24, height: 24, color: "green" }} onClick={() => {tooltipAction(tool, rowData) }} /></div>);
  }

  const RenderSlotIndicator = (props) => {
    const {indicators, tool, rowData} = props;
    const indicator = __.has(indicators, tool) ? indicators[tool] : {color: "none", status: "NOT REQUIRED"};
    let color = indicator.color;

    const slotInfo = useSlotInfo({queryClient, extraKeys:[rowData?.tripLegUuid||'EMPTY'], cached: true});

    return (
      <LightTooltip title={renderSlotInfo(slotInfo?.data?.inboundSlotInfo, slotInfo?.data?.outboundSlotInfo, rowData.originAirportCode, rowData.destinationAirportCode)} arrow>
        {
          (rowData && rowData.transportType !== "PRIMARY")?
            <></> :
          indicator.status === "REQUIRED"?
            <div style={{textAlign: "center"}}><WarningTwoTone style={{width: 24, height: 24, color: color }} onClick={() => {tooltipAction(tool, rowData) }}/></div> :
          indicator.status === "INCOMPLETE" ?
            <div style={{textAlign: "center"}}><WarningTwoTone style={{width: 24, height: 24, color: color}} onClick={() => {tooltipAction(tool, rowData) }}/></div> :
            <></>
        }
      </LightTooltip>
    );
  }

  const tripSearch = (fieldname) => {
    return  (term, rowData) => {
      const termUpper = `${term}`.replace("-","").toUpperCase();
      if (!__.has(rowData, fieldname)) return __.isEmpty(term);

      const tripPrefix =  rowData["tripPrefix"];
      const tripNo = rowData["tripNo"];

      if (`${tripPrefix}${tripNo}`.replace("-","").startsWith(termUpper)) return true;
      if (`${tripNo}`.startsWith(termUpper)) return true;

      return false;
    }
  }

  const crewSearch = (filedName) => {
    return  (term, rowData) => {
      if (!__.has(rowData, filedName)) return __.isEmpty(term);
      if (!Array.isArray(rowData[filedName])) return __.isEmpty(term);

      if (rowData[filedName].length.toString() === term) return true;

      return false;
    }
  }

  const getFlightColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize: 12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      filterCellStyle: { fontSize: "0.7rem", padding: "4px 2px" },
      editable: "never",
      sorting: false,
      resizable: false,
      emptyValue: "",
      initialEditValue: "",
    };

    const cellStyle = { fontSize: 12, padding: "4px 2px" };
    const borderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #f5f5f5" };
    const invisibleBorderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const boldInvisibleBorderedCellStyle = { fontSize: 13, fontWeight: 800, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const borderedHeaderStyle = { borderLeft: "0px solid rgb(63, 63, 63)", borderRight: "1px solid rgb(63, 63, 63)" };
    const headerStyle = { fontSize: 12, padding: "4px 6px" };
    const filterStyle = { fontSize: 12, padding: "9px 4px", marginLeft: -12 };
    // const borderedFilterStyle = {}; //{ borderLeft: "0px solid #ffffff", borderRight: "1px solid #ffffff" };

    return [
      //{...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(70, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "Flight",
        field: "flightNo",
        editable: "never",
        filterComponent: FullWidthFilter,
        render: rowData => {
          return ( <span>{`${rowData.transportType === "TRUCK" ? `Truck` : `${rowData.airlineCode}-${rowData.flightNo}`}`}</span>);
        },
        customFilterAndSearch: flightNumberSearch('flightNo')
      },
      {
        ...baseColumn,
        ...ColumnWidth(50, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "Day",
        field: "flightDate",
        render: rowData => {
          const flightDate = moment(rowData.flightDate, "YYYY-MM-DD HH:mm:ss");
          return ( <div style={{textAlign: "center"}}>{flightDate.isoWeekday()}</div>);
        },
        filterComponent: FullWidthFilter,
        customFilterAndSearch: dowNumberSearch('flightDate')
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "Date",
        field: "flightDate",
        render: rowData => {
          const flightDate = moment(rowData.flightDate, "YYYY-MM-DD HH:mm:ss");
          return ( <span>{flightDate.format("DD MMM")}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "STD",
        field: "scheduledDepartureTimeUTC",
        render: rowData => {
          return ( <span>{ moment.utc(rowData.scheduledDepartureTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm")}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: props.showAtd?"ATD":"ETD",
        field: props.showAtd?"actualDepartureTimeUTC":"estimatedDepartureTimeUTC",
        render: rowData => {
          return ( <span>{ moment(props.showAtd?rowData.actualDepartureTimeUTC:rowData.estimatedDepartureTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm")}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "ADEP",
        field: "originAirportCode",
        filterComponent: FullWidthFilter,
        render: rowData => {
          if (props.showIcao) return (<span><AirportIcao airportCode={rowData.originAirportCode}/></span>);
          return ( <span>{rowData.originAirportCode}</span>);
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "ADES",
        field: "destinationAirportCode",
        filterComponent: FullWidthFilter,
        render: rowData => {
          if (props.showIcao) return (<span><AirportIcao airportCode={rowData.destinationAirportCode}/></span>);
          return ( <span>{rowData.destinationAirportCode}</span>);
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "STA",
        field: "scheduledArrivalTimeUTC",
        render: rowData => {
          return ( <span>{ moment(rowData.scheduledArrivalTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm")}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: props.showAtd?"ATA":"ETA",
        field: props.showAtd?"actualArrivalTimeUTC":"estimatedArrivalTimeUTC",
        render: rowData => {
          return ( <span>{ moment(props.showAtd?rowData.actualArrivalTimeUTC:rowData.estimatedArrivalTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm")}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "ALTN",
        field: "alternateAirportCode",
        render: rowData => {
          if (rowData.transportType === "TRUCK") return '';
          if (props.showIcao) return (<span><AirportIcao airportCode={rowData.alternateAirportCode}/></span>);
          return ( <span>{rowData.alternateAirportCode}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "ACFT",
        field: "aircraftReg",
        filterComponent: FullWidthFilter,
        render: rowData => {
          if (rowData.transportType === "TRUCK") {
            return (
              <div>
                <BuildTruckRouteIconFromLegs legs={[rowData]} style={{paddingRight: 5, paddingBottom: 2}}/>
              </div>
            )
          }
          return ( <span>{ `${rowData.transportType === "TRUCK" ? `` : `${rowData.aircraftReg}`}`}</span>);
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(95, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "Trip",
        field: "tripPrefix",
        render: rowData => {
          return ( <span>{ `${rowData.tripPrefix}-${rowData.tripNo}`}</span>);
        },
        filterComponent: FullWidthFilter,
        customFilterAndSearch: tripSearch('tripPrefix')
      },
      {
        ...baseColumn,
        ...ColumnWidth(5, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "",

        filtering: false,
      },
      {
        ...baseColumn,
        ...ColumnWidth(30, 2, undefined, cellStyle, headerStyle, undefined, false),
        title: "Cargo",
        field: "flightListIndicators",
        filtering: false,
        render: rowData => renderIndicator(rowData.flightListIndicators, "cargo", rowData)
      },
      {
        ...baseColumn,
        ...ColumnWidth(90, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "",
        field: "flightNo", // Ensure that column is never null - MaterialTable will not display null columns
        filtering: false,
        render: rowData => (<CargoGrossWeight tripLegUuid={rowData.tripLegUuid} eventBus={eventBus} record={rowData} classes={classes}/>)
      },
      {
        ...baseColumn,
        ...ColumnWidth(30, 2, undefined, cellStyle, headerStyle, undefined, false),
        title: "Crew",
        field: "flightListIndicators",
        filtering: false,
        render: rowData => renderIndicator(rowData.flightListIndicators, "crew", rowData)
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "",
        field: "crew",
        filtering: false,
        render: rowData => {
          const crewNumber = rowData.crew && Array.isArray(rowData.crew) ? rowData.crew.length : "";
          return ( <div style={{textAlign: "center"}} onClick={() => {tooltipAction("crew", rowData) }}>{ `${crewNumber}`}</div>);
        },
        filterComponent: FullWidthFilter,
        customFilterAndSearch: crewSearch("crew")
      },
      {
        ...baseColumn,
        ...ColumnWidth(50, 4, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "PAX",
        field: "passengers",
        filtering: false,
        render: rowData => {
          const paxNumber = rowData.passengers && Array.isArray(rowData.passengers) ? rowData.passengers.length : 0;
          return ( <div style={{textAlign: "center"}} onClick={() => {tooltipAction("pax", rowData) }}>{ `${paxNumber}`}</div>);
        },
        filterComponent: FullWidthFilter,
        customFilterAndSearch: crewSearch("passengers")
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "In Slot",
        field: "flightListIndicators",
        filtering: false,
        render: rowData =>
          <RenderSlotIndicator
            indicators={rowData.flightListIndicators}
            tool={"slotIn"}
            rowData={rowData}
          />
          // <LightTooltip title={renderSlotInfo(rowData.tripLegUuid, rowData.originAirportCode, rowData.destinationAirportCode)} arrow>
          //   {renderIndicator(rowData.flightListIndicators, "slotIn", rowData)}
          // </LightTooltip>
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Out Slot",
        field: "flightListIndicators",
        filtering: false,
        render: rowData =>
          <RenderSlotIndicator
            indicators={rowData.flightListIndicators}
            tool={"slotOut"}
            rowData={rowData}
          />
          // <LightTooltip title={renderSlotInfo(rowData.tripLegUuid, rowData.originAirportCode, rowData.destinationAirportCode)} arrow>
          //   {renderIndicator(rowData.flightListIndicators,"slotOut", rowData)}
          // </LightTooltip>
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "JetPlan",
        field: "flightListIndicators",
        filtering: false,
        render: rowData => renderIndicator(rowData.flightListIndicators, "jetplan", rowData)
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Permits",
        field: "flightListIndicators",
        filtering: false,
        //render: rowData => renderIndicator(rowData.permits, null, null)
        render: rowData => {
          const uuid = rowData.recordType === "SCHEDULED" ? rowData.tripUuid : rowData.tripLegUuid;
          const tripData = {recordType: rowData.recordType, uuid, airlineCode: rowData.airlineCode, flightNo: rowData.flightNo};
          return (
            <LightTooltip title={<div style={{width: 400}}><PermitsScreen tripData={tripData} classes={classes} eventBus={eventBus} colours={colours}/></div>} arrow placement={"left"} interactive>
              {renderIndicator(rowData.flightListIndicators, "permits", rowData)}
            </LightTooltip>
          )}
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Fuel Order",
        field: "flightListIndicators",
        filtering: false,
        // render: rowData => renderIndicator(rowData.flightListIndicators, "fuel", null)
        render: rowData => {
          const uuid = rowData.recordType === "SCHEDULED" ? rowData.tripUuid : rowData.tripLegUuid;
          const tripData = {recordType: rowData.recordType, uuid, airlineCode: rowData.airlineCode, flightNo: rowData.flightNo, tripUuid: rowData.tripUuid};
          return (
            <LightTooltip title={<div style={{width: 400}}><FuelScreen tripData={tripData} classes={classes} eventBus={eventBus} colours={colours}/></div>} arrow placement={"left"} interactive>
              {renderIndicator(rowData.flightListIndicators, "fuel", rowData)}
            </LightTooltip>
          )}
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 8, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Catering",
        field: "flightListIndicators",
        filtering: false,
        render: rowData => renderIndicator(rowData.flightListIndicators, "catering", rowData)
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Dispatch",
        field: "flightListIndicators",
        filtering: false,
        render: rowData => renderIndicator(rowData.flightListIndicators, "checklist", rowData)
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Ground",
        field: "flightListIndicators",
        filtering: false,
        render: rowData => {
          if (__.has(rowData.flightListIndicators, "ffm") && __.has(rowData.flightListIndicators.ffm, "status") && rowData.flightListIndicators.ffm.status === "COMPLETED") {
            return (
              <div style={{textAlign: "center"}}>
                <IconButton onClick={() => {eventBus.trigger("FlightManifests.open", rowData) }} size="small">
                  <FfmIcon />
                </IconButton>
              </div>
            )
          }
          return <></>
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(100, 4, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Special Loads",
        field: "flightListIndicators",
        filtering: false,
        render: rowData => <SpecialLoads classes={classes} eventBus={eventBus} tripLegUuid={rowData.tripLegUuid} />
      },
      {
        ...baseColumn,
        title: "Notes",
        field: "tripUuid",
        filtering: false,
        render: rowData => {
          const uuid = rowData.recordType === "SCHEDULED" ? rowData.tripUuid : rowData.tripLegUuid;
          const tripData = {recordType: rowData.recordType, uuid, airlineCode: rowData.airlineCode, flightNo: rowData.flightNo};
          return (
            <LightTooltip title={<div style={{width: 400}}><NotesViewer displayOnly={true} tripData={tripData} classes={classes} eventBus/></div>} arrow placement={"left"} interactive>
              {<div style={{textAlign:"center"}}><NotesCount tripData={tripData} classes={classes} /></div>}
            </LightTooltip>
        )}
      },
      {
        ...baseColumn,
        ...ColumnWidth(5, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "",

        filtering: false,
      },
    ];
  };

  const FlightRows = memo((props) => {
    const theader = tableContainerRef.current?.querySelector('thead');
    const thead = theader ? theader.clientHeight:30;
    const bodyHeight = tableHeight - thead;

    return (
      <MaterialTable
        icons={{ Filter: React.forwardRef((props, ref) => <Icon className="filterIcon">filter_list</Icon> )}}
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        onRowClick={handleRowClick}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "4px 2px",
          },
          filterCellStyle: {
            padding: "4px 2px",
            fontSize: "0.7rem",
          },
          headerRowStyle: {
            display: "table",
            width: "100%",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "4px 2px",
          },
          filterRowStyle: {
            paddingLeft: state.canEdit ? 0 : 0,
            width: state.canEdit ? "calc(100% - 20px)" : "100%",
          },
          highlightRowStyle: {backgroundColor: "#F69259"},
          rowStyle: (rowData)=> ({opacity: rowData.cancelled?0.5:1, borderBottom: `${rowData.dateChange?"3":"1"}px solid rgba(224, 224, 224, 1)`}),
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          paging: false,
          filtering: true,
          search: false,
          title: "Flight List"
        }}
        components={{
          ...defaultComponentsScroll,
          Body: (props) => (
            <MaterialTableBody
              {...props}
              rowHeight={46}
              overscanRowCount={20}
              addVirtualScrolling={true}
              bodyHeight={bodyHeight}
              headerHeight={thead}
              filterRowHeight={46}
            />
          ),
          Toolbar: (toolbarProps) => {
            return (
              <div style={{ position: "relative" }}>
                <MTableToolbar {...toolbarProps} />
                <div style={{position: "absolute", left: 150, top: 16}}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>Show ICAO Airport Codes</Typography>
                    <Switch checked={props.showIcao} onChange={()=>{eventBus.trigger("FlightList.toggleIcao");}}/>
                  </Stack>
                </div>
                {month > 0 ?
                  <div style={{position: "absolute", left: 420, top: 16}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography>Show Actuals Times</Typography>
                      <Switch checked={props.showAtd} onChange={()=>{eventBus.trigger("FlightList.toggleAtd");}}/>
                    </Stack>
                  </div>
                  : null
                }
              </div>
            );
          },
        }}
        localization={{
          header: {
            actions: `Actions${String.fromCharCode(160)}`
          },
        }}
        actions={[
          (rowData) => (
            {
              hidden: false,
              //disabled: (rowData.uniqueCostingNo === null || rowData.uniqueCostingNo === ""),
              icon: () => (
                <FlightTooltip editing={true} colours={colours} onChange={tooltipAction} record={rowData}>
                  <div style={{textAlign: "center", width: 50}}>
                    <ChecklistIcon style={{width: 24, height: 24, color: (rowData.tableData.id === selectedRow ? '#ffffff' : theme.palette.primary.main) }}/>
                  </div>
                </FlightTooltip>
              ),
              iconProps: { style: {color: (rowData.tableData.id === selectedRow ? '#ffffff' : theme.palette.primary.main), marginLeft: 30}},
              tooltip: 'Flight Checks',
              isFreeAction: false,
              onClick: (e, rowData) => {
                eventBus.trigger("FlightTooltip.open.open");
              }
            }
          ),
          {
            hidden: false,
            disabled: false,
            icon: () => {
              return(
                <IconButtonMenu
                  icon={<SaveAlt/>}
                  size={"small"}
                  offset={-1}
                  style={{ color: theme.palette.primary.main }}
                  tooltip={"Flight Schedule Reports"}
                  menuItems={listActionOptions}
                  callback={executeListAction}
                  callbackParams={{}}
                />)
            },
            isFreeAction: true,
          },
          {
            hidden: false,
            disabled: false,
            icon: () => {
              return(
                <IconButtonMenu
                  icon={<Email/>}
                  size={"small"}
                  offset={-1}
                  style={{ color: theme.palette.primary.main }}
                  tooltip={"Flight Schedule Reports"}
                  menuItems={sendActionOptions}
                  callback={executeEmailAction}
                  callbackParams={{}}
                />)
            },
            isFreeAction: true,
          },
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
        ]}
        title="Flights"
        columns={getFlightColumns({canEdit: state.canEdit, showIcao: props.showIcao, showAtd: props.showAtd})}
        data={props.data}
      />
    )
  });

  const [tableContainerRef, { width: tableWidth, height: tableHeight}] = useDimensions({});
  if (!defaultValues || __.isEmpty(defaultValues))
    getDefaultValues();
  if (!scheduleDefaultValues || __.isEmpty(scheduleDefaultValues))
    getScheduleDefaultValues();

  return (isAuthenticated &&
    <RefreshCheck
      year={props.year}
      month={props.month}
      refreshEventName="FlightList.TripAction.RefreshList"
      checkType="Flight List"
    >
      <div ref={tableContainerRef} className={state.canEdit?classes.FlightListEdit:classes.FlightList} style={{width: "100%", minWidth: "100%", marginLeft: 20, marginRight: 20, noWidth: "calc(100% - 24px)"}}>
        <FlightRows data={flights.data} showIcao={props.showIcao} showAtd={props.showAtd}/>
        <AirportSelectionDialog
          open={selectAirport.open}
          withSupplier={selectAirport.mode === "email"}
          onChange={(airport, companyUuid, excludeAcmi) => {
            if (airport && companyUuid) {
              downloadFlightSchedule("airport", airport, selectAirport.mode === "download", companyUuid, null, excludeAcmi);
            } else if (airport) {
              downloadFlightSchedule("airport", airport, selectAirport.mode === "download", null, null, excludeAcmi);
            }
            setSelectAirport({...selectAirport, open: false, airport: airport})
          }}
        />
        <CustomerSelectionDialog
          open={selectCustomer.open}
          airline={selectCustomer.airline}
          onChange={(customerUuid, companyName, airlineCode) => {
            if (selectCustomer.airline && customerUuid) {
              downloadFlightSchedule("airline", airlineCode, selectCustomer.mode === "download");
            }
            else if (customerUuid) {
              downloadFlightSchedule("customer", customerUuid, selectCustomer.mode === "download", customerUuid, companyName);
            }
            setSelectCustomer({...selectCustomer, open: false, customerUuid: null, airline: null})
          }}
        />
        <AircraftFuelLogDialog {...aircraftFuelLogDialogProps} />
        <EditTripDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdate={handleUpdateTrip}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditCrewDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditPaxDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditServicesDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdateFlight={handleSaveServices}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditCateringDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditNotesDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditDeadloadDialog
          classes={classes}
          systemData={SystemData}
          extraKeys={extraKeys}
          formatChars={formatChars}
          canEdit={state.canEdit}
        />
        <EditChecklistDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditFlightDocumentsDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditPermitsDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditMapDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditFlightCargoDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditFlightSlotsDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <FuelPolicyDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          classes={classes}
          // afterSave={()=>{eventBus.trigger("RouteView.refreshLeg")}}
        />
        <FlightManifestsDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          // handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
        <EditFlightMessagesDialog
          classes={classes}
          systemData={SystemData}
          extraKeys={extraKeys}
          formatChars={formatChars}
          canEdit={state.canEdit}
        />
      </div>
      <ViewBooking />
    </RefreshCheck>
  );

}

export default FlightList;
